<script lang="ts">
  /**
   * Indicateur visuel qui apparaît au-dessus de la liste pendant un
   * pull-down. Reçoit son mode via l'attribut `data-pull-mode` posé par
   * l'action `pullDown` (cf. gestures/pullDown.ts) :
   *
   *   absent          → invisible (snap-back imminent)
   *   "search"        → icône loupe + "Relâcher pour rechercher"
   *   "refresh"       → icône refresh + "Relâcher pour actualiser"
   *
   * Posé en absolute au-dessus du contenu inbox. Quand le wrapper
   * pullDown translate vers le bas, l'indicateur (positionné top:-60px)
   * devient visible dans l'espace libéré.
   */

  interface Props {
    /** Bind l'élément DOM pour le passer à pullDown comme `indicator`. */
    el?: HTMLDivElement;
  }

  let { el = $bindable() }: Props = $props();
</script>

<div class="pull-indicator" bind:this={el} aria-hidden="true">
  <div class="hint search-hint">
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7"/>
      <path d="M20 20l-3.5-3.5"/>
    </svg>
    <span>Rechercher</span>
    <span class="hint-arrow">←</span>
    <span class="hint-tail">Actualiser</span>
  </div>
  <div class="hint refresh-hint">
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8"/>
      <path d="M21 3v5h-5"/>
      <path d="M21 12a9 9 0 0 1-15.5 6.3L3 16"/>
      <path d="M3 21v-5h5"/>
    </svg>
    <span>Actualiser</span>
  </div>
</div>

<style>
  .pull-indicator {
    position: absolute;
    top: -60px;
    left: 0;
    right: 0;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    color: var(--fg-subtle);
    user-select: none;
    z-index: 1;
  }

  .hint {
    display: none;
    align-items: center;
    gap: 8px;
    font-family: var(--font-sans);
    font-size: 12.5px;
    font-weight: 500;
    letter-spacing: -0.005em;
  }

  /* Mode "search" (default pendant le pull) : montre les deux hints
     côte à côte avec une flèche → invite à découvrir le swipe gauche.
     `:global()` car `data-pull-mode` est posé dynamiquement par
     `pullDown.ts`, hors connaissance du compilateur Svelte. */
  :global(.pull-indicator[data-pull-mode="search"]) .search-hint { display: inline-flex; }
  :global(.pull-indicator[data-pull-mode="refresh"]) .refresh-hint {
    display: inline-flex;
    color: var(--accent);
    font-weight: 600;
  }

  .hint-arrow {
    font-size: 13px;
    color: var(--fg-subtle);
    margin: 0 4px;
  }
  .hint-tail {
    color: var(--fg-subtle);
  }
</style>
